import { createContext, useContext } from 'react';
import { cn, formatNumber } from '@/lib/utils';
import { PANEL, PANEL_LIFT } from '@/lib/surface';
import { Card } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import {
  type Sentiment,
  sentimentText,
  sentimentIconBg,
  sentimentAccentBorder,
  sentimentTrendPill,
} from '@/lib/design-tokens';

interface MetricTrend {
  direction: 'up' | 'down' | 'neutral';
  value: string;
  text?: string;
}

interface MetricCardProps {
  label: string;
  value: string | number;
  trend?: MetricTrend;
  icon?: React.ReactNode;
  sentiment?: Sentiment;
  subtitle?: string;
  prefix?: string;
  suffix?: string;
  className?: string;
}

/**
 * Renders a metric value.
 *
 * This used to count up from zero over 1.5s. On a dashboard that means every
 * number on the page reads wrong until the animation lands, and the figure is
 * the whole point of the card — so the value is now painted directly.
 *
 * Numbers are grouped here rather than at each call site. Cards were passing
 * raw numbers straight through, so a total inventory value rendered as
 * "$2822466" — a figure nobody can read at a glance, which defeats the card.
 * Callers that need their own formatting still pass a string, and strings are
 * left exactly as given.
 */
function MetricValue({
  value,
  prefix = '',
  suffix = '',
}: Pick<MetricCardProps, 'value' | 'prefix' | 'suffix'>) {
  return (
    <>
      {prefix}
      {typeof value === 'number' ? formatNumber(value) : value}
      {suffix}
    </>
  );
}

/** Trend pill + comparison text, shared by both metric layouts. */
function MetricTrendPill({ trend, subtitle }: { trend?: MetricTrend; subtitle?: string }) {
  if (trend) {
    return (
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
            sentimentTrendPill[trend.direction]
          )}
        >
          {trend.direction === 'up' && <TrendingUp className="h-3 w-3" />}
          {trend.direction === 'down' && <TrendingDown className="h-3 w-3" />}
          {trend.direction === 'neutral' && <Minus className="h-3 w-3" />}
          <span>{trend.value}</span>
        </span>
        {trend.text && <span className="text-xs text-muted-foreground">{trend.text}</span>}
      </div>
    );
  }

  if (subtitle) {
    return <span className="text-xs text-muted-foreground">{subtitle}</span>;
  }

  return null;
}

/**
 * True for anything rendered inside a <KPIStrip>. Lets MetricCard render as a
 * segment there without every page having to swap component names.
 */
const StripContext = createContext(false);

export function MetricCard({
  label,
  value,
  trend,
  icon,
  sentiment = 'neutral',
  subtitle,
  prefix = '',
  suffix = '',
  className,
}: MetricCardProps) {
  const inStrip = useContext(StripContext);

  // Inside a strip the card chrome belongs to the strip, not each cell.
  if (inStrip) {
    return (
      <MetricSegment
        label={label}
        value={value}
        trend={trend}
        icon={icon}
        sentiment={sentiment}
        subtitle={subtitle}
        prefix={prefix}
        suffix={suffix}
        className={className}
      />
    );
  }

  // Generate a unique ID for accessibility
  const labelId = `metric-${label
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')}`;

  return (
    <Card
      role="region"
      aria-labelledby={labelId}
      className={cn(
        // p-3 and no min-height: main tightened the KPI cards after this
        // branch forked and that density is worth keeping.
        PANEL,
        'p-3',
        'border-l-[3px]',
        sentimentAccentBorder[sentiment],
        PANEL_LIFT,
        className
      )}
    >
      <div className="flex items-start justify-between mb-2">
        {/* Label — uppercase, smaller, wider tracking */}
        <span
          id={labelId}
          className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
        >
          {label}
        </span>

        {/* Icon — compact container */}
        {icon && (
          <div
            className={cn(
              'w-8 h-8 rounded-lg flex items-center justify-center',
              sentimentIconBg[sentiment]
            )}
          >
            {icon}
          </div>
        )}
      </div>

      {/* Value — matches MetricSegment's 24px / 700 */}
      <div
        className={cn('text-2xl font-bold tracking-tight', sentimentText[sentiment])}
        aria-live="polite"
      >
        <MetricValue value={value} prefix={prefix} suffix={suffix} />
      </div>

      {/* Subtitle or Trend */}
      <div className="flex items-center gap-2 mt-1">
        <MetricTrendPill trend={trend} subtitle={subtitle} />
      </div>
    </Card>
  );
}

/**
 * A single cell of a <KPIStrip>. Same props as MetricCard, but laid out as an
 * icon tile beside the label/value pair rather than as a standalone card.
 */
export function MetricSegment({
  label,
  value,
  trend,
  icon,
  sentiment = 'neutral',
  subtitle,
  prefix = '',
  suffix = '',
  className,
}: MetricCardProps) {
  const labelId = `metric-${label
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')}`;

  return (
    <div
      role="region"
      aria-labelledby={labelId}
      // Top-aligned, not centred: segments with a trend pill are taller, and
      // centring would push their label/value up out of line with the rest.
      className={cn('flex items-start gap-3 bg-card p-5', className)}
    >
      {icon && (
        <div
          className={cn(
            'w-10 h-10 rounded-lg flex items-center justify-center shrink-0',
            sentimentIconBg[sentiment]
          )}
        >
          {icon}
        </div>
      )}

      <div className="min-w-0">
        <div
          id={labelId}
          className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
        >
          {label}
        </div>

        <div
          className="text-2xl font-bold tracking-tight text-foreground truncate"
          aria-live="polite"
        >
          <MetricValue value={value} prefix={prefix} suffix={suffix} />
        </div>

        {(trend || subtitle) && (
          <div className="flex items-center gap-2 mt-0.5">
            <MetricTrendPill trend={trend} subtitle={subtitle} />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Segmented KPI strip — one card divided into cells by hairline rules, rather
 * than a grid of separate cards.
 *
 * The rules come from a 1px grid gap over a border-coloured background, which
 * keeps them correct however the cells wrap at each breakpoint.
 */
interface KPIStripProps {
  children: React.ReactNode;
  columns?: 1 | 2 | 3 | 4 | 5 | 6;
  className?: string;
}

export function KPIStrip({ children, columns = 4, className }: KPIStripProps) {
  return (
    <StripContext.Provider value={true}>
      <div className={cn(PANEL, 'overflow-hidden')}>
        {/* Dividers are drawn per cell rather than as a 1px grid gap: a gap
            would also show through empty slots when the item count doesn't
            fill the last row. The -mr-px/-mb-px pull the outermost borders
            under the container's own border, so only interior rules show.

            className lands on the grid, not the wrapper — callers pass column
            overrides like "lg:grid-cols-5", which only work here. */}
        <div
          className={cn(
            'grid -mr-px -mb-px',
            '[&>*]:border-r [&>*]:border-b [&>*]:border-border',
            // Every count reaches its full width at lg. Four used to wait for
            // xl, so between 1024 and 1280 a four-metric strip broke into two
            // rows while a five-metric one beside it stayed on one — the same
            // viewport giving two different densities depending on the count.
            columns === 1 && 'grid-cols-1',
            columns === 2 && 'grid-cols-1 sm:grid-cols-2',
            columns === 3 && 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
            columns === 4 && 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
            columns === 5 && 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5',
            columns === 6 && 'grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-6',
            className
          )}
        >
          {children}
        </div>
      </div>
    </StripContext.Provider>
  );
}

/**
 * Historical name for the KPI row. It now renders the segmented strip, so the
 * pages using it pick up the new treatment without needing to be edited.
 */
export const KPIGrid = KPIStrip;

// Grouped KPI cards with dashed border (LognAI pattern)
interface GroupedKPIProps {
  title?: string;
  children: React.ReactNode;
  className?: string;
}

export function GroupedKPI({ title, children, className }: GroupedKPIProps) {
  return (
    <div className={cn('rounded-lg border border-dashed border-border p-4', className)}>
      {title && (
        <h3 className="text-base font-semibold mb-4 uppercase text-muted-foreground tracking-wider">
          {title}
        </h3>
      )}
      {children}
    </div>
  );
}
